/**
 * ============================================================
 * EMULADOR DE BOT DE WHATSAPP
 * ============================================================
 * Lee el JSON exportado por el Diseñador Visual y simula
 * la conversación completa en una interfaz idéntica a WhatsApp.
 *
 * Soporta: sendMessage, media, replyButtons, listButtons,
 * askQuestion/Email/Number/Phone/Date/File/Address/Url,
 * conditions, setField, formulas, jumpTo, keywordOptions,
 * keywordJump, globalKeywords, leadScoring, goal, abTest,
 * sendTemplate, contactSubscribe, goodbye.
 */

import { useState, useRef, useEffect, useCallback } from "react";

// ============================================================
// UTILIDADES DEL MOTOR DE FLUJO
// ============================================================

const interpolate = (text, vars) => {
  if (!text) return "";
  return String(text).replace(/\{\{(\w+)\}\}/g, (_, n) => vars[n] ?? `{{${n}}}`);
};

// Renderiza markdown de WhatsApp: *bold*, _italic_, ~tachado~, `mono`
const renderWAText = (text) => {
  if (!text) return null;
  const re = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`)/g;
  const parts = [];
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const raw = m[0], inner = raw.slice(1, -1);
    if (raw[0] === '*') parts.push(<strong key={m.index}>{inner}</strong>);
    else if (raw[0] === '_') parts.push(<em key={m.index}>{inner}</em>);
    else if (raw[0] === '~') parts.push(<s key={m.index}>{inner}</s>);
    else parts.push(<code key={m.index} style={{ fontFamily: "monospace", fontSize: "0.9em" }}>{inner}</code>);
    last = m.index + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const evalCond = (c, vars) => {
  const val = String(vars[c.variable] ?? "");
  const tgt = String(c.value ?? "");
  switch (c.operator) {
    case "equals":       return val.toLowerCase() === tgt.toLowerCase();
    case "not_equals":   return val.toLowerCase() !== tgt.toLowerCase();
    case "contains":     return val.toLowerCase().includes(tgt.toLowerCase());
    case "not_contains": return !val.toLowerCase().includes(tgt.toLowerCase());
    case "starts_with":  return val.toLowerCase().startsWith(tgt.toLowerCase());
    case "ends_with":    return val.toLowerCase().endsWith(tgt.toLowerCase());
    case "text_equals":  return val === tgt;
    case "greater_than": return parseFloat(val) > parseFloat(tgt);
    case "less_than":    return parseFloat(val) < parseFloat(tgt);
    case "length_gt":    return val.length > parseFloat(tgt);
    case "length_lt":    return val.length < parseFloat(tgt);
    case "is_set":       return val !== "" && val !== "undefined" && val !== "null";
    case "is_not_set":   return val === "" || val === "undefined" || val === "null";
    case "is_empty":     return val === "";
    case "is_not_empty": return val !== "";
    default:             return false;
  }
};

const evalConditions = (node, vars) => {
  const conds = node.data.conditions || [];
  if (!conds.length) return false;
  return node.data.logicOp === "OR"
    ? conds.some(c => evalCond(c, vars))
    : conds.every(c => evalCond(c, vars));
};

const evalFormula = (formula, vars) => {
  try {
    const expr = formula.replace(/\{\{(\w+)\}\}/g, (_, n) => {
      const v = parseFloat(vars[n]);
      return isNaN(v) ? 0 : v;
    });
    // eslint-disable-next-line no-new-func
    return String(Function(`"use strict"; return (${expr})`)());
  } catch { return "0"; }
};

const uid = () => Math.random().toString(36).slice(2, 9);
const timeStr = () => new Date().toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" });
const sleep = ms => new Promise(r => setTimeout(r, ms));

// Convierte emociones/acciones entre paréntesis a emojis (respuestas de IA)
const EMOTION_MAP = {
  "suspirando": "😔", "suspira": "😔", "suspiro": "😔",
  "riendo": "😄", "ríe": "😄", "risa": "😄", "risas": "😄", "carcajada": "😂",
  "sonriendo": "😊", "sonríe": "😊", "sonrisa": "😊",
  "pensando": "🤔", "piensa": "🤔", "reflexionando": "🤔",
  "triste": "😢", "tristeza": "😢", "llorando": "😢", "llora": "😢",
  "enojado": "😠", "enojada": "😠", "enojo": "😠", "molesto": "😠", "molesta": "😠",
  "sorprendido": "😮", "sorprendida": "😮", "sorpresa": "😮",
  "nervioso": "😰", "nerviosa": "😰", "nerviosamente": "😰",
  "emocionado": "🤩", "emocionada": "🤩", "entusiasmado": "🤩",
  "confundido": "😕", "confundida": "😕", "confusión": "😕",
  "asintiendo": "👍", "asiente": "👍", "afirmando": "👍",
  "negando": "🙅", "niega": "🙅",
  "feliz": "😊", "contento": "😊", "contenta": "😊", "alegre": "😊",
  "preocupado": "😟", "preocupada": "😟", "preocupación": "😟",
  "dudando": "🤨", "duda": "🤨", "dudoso": "🤨",
  "silencio": "🤐", "callado": "🤐",
  "mirando": "👀", "observando": "👀",
  "aplaudiendo": "👏", "aplaude": "👏",
  "abrazando": "🤗", "abrazo": "🤗",
  "gritando": "😤", "grita": "😤",
  "cansado": "😴", "cansada": "😴", "agotado": "😴",
  "asustado": "😨", "asustada": "😨",
  "avergonzado": "😳", "avergonzada": "😳",
  "aliviado": "😌", "aliviada": "😌", "alivio": "😌",
};

const convertEmotions = (text) => {
  return text.replace(/\(([^)]{1,50})\)/g, (match, inner) => {
    const lower = inner.toLowerCase().trim();
    if (EMOTION_MAP[lower]) return EMOTION_MAP[lower];
    for (const word of lower.split(/\s+/)) {
      if (EMOTION_MAP[word]) return EMOTION_MAP[word];
    }
    return match; // mantener si no hay coincidencia
  });
};

// ============================================================
// COMPONENTES DE BURBUJA
// ============================================================

const BotBubble = ({ msg }) => (
  <div style={{ marginBottom: 6 }}>
    <div style={{
      maxWidth: "85%", background: "#fff",
      borderRadius: "2px 12px 12px 12px",
      padding: "7px 10px 5px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.13)",
    }}>
      {msg.image && (
        <img src={msg.image} alt="" style={{
          width: "100%", borderRadius: 6, marginBottom: (msg.text || msg.caption) ? 6 : 2,
          maxHeight: 190, objectFit: "cover", display: "block",
        }} onError={e => { e.target.style.display = "none"; }} />
      )}
      {msg.mediaUrl && (
        <div style={{
          background: "#f0f0f0", borderRadius: 6, padding: "8px 12px",
          marginBottom: (msg.text || msg.caption) ? 6 : 2, fontSize: 12, color: "#555",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span>{msg.mediaType === "video" ? "🎬" : msg.mediaType === "audio" ? "🔊" : "📄"}</span>
          <span>{msg.mediaType === "video" ? "Video" : msg.mediaType === "audio" ? "Audio" : "Documento"}</span>
        </div>
      )}
      {msg.caption && (
        <div style={{ fontSize: 13, color: "#555", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.4, marginBottom: msg.text ? 4 : 0 }}>
          {msg.caption}
        </div>
      )}
      {msg.text && (
        <div style={{ fontSize: 14, color: "#111", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45 }}>
          {renderWAText(msg.text)}
        </div>
      )}
      <div style={{ fontSize: 11, color: "#aaa", textAlign: "right", marginTop: 3 }}>{msg.time}</div>
    </div>
  </div>
);

const UserBubble = ({ msg }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
    <div style={{
      maxWidth: "85%", width: "fit-content", background: "#dcf8c6",
      borderRadius: "12px 2px 12px 12px",
      padding: "7px 10px 5px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.13)",
    }}>
      {msg.image && (
        <img src={msg.image} alt="" style={{
          width: "100%", borderRadius: 6, marginBottom: msg.text ? 6 : 2,
          maxHeight: 190, objectFit: "cover", display: "block",
        }} />
      )}
      {msg.text && (
        <div style={{ fontSize: 14, color: "#111", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45 }}>
          {renderWAText(msg.text)}
        </div>
      )}
      <div style={{ fontSize: 11, color: "#aaa", textAlign: "right", marginTop: 3 }}>
        {msg.time} <span style={{ color: "#4fc3f7" }}>✓✓</span>
      </div>
    </div>
  </div>
);

const TypingDots = () => (
  <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 6 }}>
    <div style={{
      background: "#fff", borderRadius: "2px 12px 12px 12px",
      padding: "11px 14px", boxShadow: "0 1px 2px rgba(0,0,0,0.13)",
    }}>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: "#bbb",
            animation: "wabounce 1.2s ease infinite",
            animationDelay: `${i * 0.2}s`,
          }} />
        ))}
      </div>
    </div>
  </div>
);

const ReplyBtns = ({ buttons, onSelect }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
    {buttons.map(b => (
      <button key={b.id} onClick={() => onSelect(b)}
        style={{
          padding: "10px 0", background: "#fff",
          border: "1px solid #25D366", borderRadius: 8,
          color: "#075E54", fontWeight: 700, fontSize: 14,
          cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          transition: "background 0.12s",
        }}
        onMouseOver={e => e.currentTarget.style.background = "#f0fff4"}
        onMouseOut={e => e.currentTarget.style.background = "#fff"}
      >{b.label}</button>
    ))}
  </div>
);

const ListSheet = ({ data, onSelect }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        background: "#fff", borderRadius: "2px 12px 12px 12px",
        padding: "8px 10px 7px", boxShadow: "0 1px 2px rgba(0,0,0,0.13)",
      }}>
        {data.body && (
          <div style={{ fontSize: 14, color: "#111", marginBottom: 7, lineHeight: 1.4 }}>{data.body}</div>
        )}
        <button onClick={() => setOpen(true)}
          style={{
            width: "100%", padding: "8px 0", background: "#e8f5e9",
            border: "1px solid #25D366", borderRadius: 6,
            color: "#075E54", fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}>
          ☰ {data.buttonText || "Ver opciones"}
        </button>
      </div>

      {open && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
          onClick={() => setOpen(false)}
        >
          <div
            style={{
              background: "#fff", width: "100%", maxWidth: 400,
              borderRadius: "18px 18px 0 0", maxHeight: "72vh", overflowY: "auto",
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: "14px 16px", borderBottom: "1px solid #eee", fontWeight: 700, fontSize: 15 }}>
              {data.header || "Elige una opción"}
            </div>
            {(data.sections || []).map((sec, si) => (
              <div key={si}>
                {sec.title && (
                  <div style={{
                    padding: "7px 16px", background: "#f5f5f5",
                    fontSize: 11, color: "#666", fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.5,
                  }}>{sec.title}</div>
                )}
                {(sec.options || []).map(opt => (
                  <div key={opt.id}
                    onClick={() => { onSelect(opt); setOpen(false); }}
                    style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
                    onMouseOver={e => e.currentTarget.style.background = "#f9f9f9"}
                    onMouseOut={e => e.currentTarget.style.background = "#fff"}
                  >
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{opt.title}</div>
                    {opt.desc && <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{opt.desc}</div>}
                  </div>
                ))}
              </div>
            ))}
            <div
              onClick={() => setOpen(false)}
              style={{ padding: 14, textAlign: "center", color: "#888", cursor: "pointer", fontSize: 14 }}
            >Cancelar</div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// DEBUG PANEL
// ============================================================

const DBG = {
  req:    { label: "→ REQ",   color: "#60a5fa", bg: "#1e3a5f" },
  res:    { label: "← RES",   color: "#34d399", bg: "#0f2a1e" },
  node:   { label: "NODE",    color: "#94a3b8", bg: "#1e293b" },
  msg:    { label: "MSG",     color: "#f1f5f9", bg: "#1e293b" },
  wait:   { label: "⏸ WAIT",  color: "#fbbf24", bg: "#2d2410" },
  branch: { label: "BRANCH",  color: "#fb923c", bg: "#2d1e10" },
  end:    { label: "END",     color: "#f87171", bg: "#2d1010" },
  error:  { label: "ERROR",   color: "#f87171", bg: "#3d1010" },
  vars:   { label: "VARS",    color: "#c084fc", bg: "#1e1030" },
};

const DebugEntry = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const s = DBG[entry.type] || DBG.node;
  return (
    <div
      onClick={() => entry.details && setOpen(o => !o)}
      style={{
        padding: "3px 10px", borderBottom: "1px solid #12121f",
        background: open ? s.bg : "transparent",
        cursor: entry.details ? "pointer" : "default",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
        <span style={{ color: "#4b5563", fontSize: 9, flexShrink: 0 }}>{entry.time}</span>
        <span style={{
          color: s.color, background: s.bg, padding: "0px 5px",
          borderRadius: 3, fontSize: 9, fontWeight: 700, flexShrink: 0,
        }}>{s.label}</span>
        <span style={{
          color: "#94a3b8", fontSize: 10, overflow: "hidden",
          textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
        }}>{entry.summary}</span>
        {entry.details && (
          <span style={{ color: "#4b5563", fontSize: 9, flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
        )}
      </div>
      {open && entry.details && (
        <pre style={{
          margin: "4px 0 2px", color: "#a9b1d6", fontSize: 10,
          whiteSpace: "pre-wrap", wordBreak: "break-all",
          background: "#0d0d1a", padding: "6px 8px", borderRadius: 4,
          maxHeight: 220, overflowY: "auto", fontFamily: "monospace",
        }}>
          {typeof entry.details === "string" ? entry.details : JSON.stringify(entry.details, null, 2)}
        </pre>
      )}
    </div>
  );
};

const DebugPanel = ({ log, onClear, mode, side }) => {
  const bottomRef = useRef(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [log]);

  return (
    <div style={{
      width: 310, height: "88vh", maxHeight: 760,
      background: "#13131f", borderRadius: 12,
      border: "1px solid #1e293b",
      display: "flex", flexDirection: "column",
      overflow: "hidden",
      marginLeft: side === "right" ? 12 : 0,
      marginRight: side === "left" ? 12 : 0,
    }}>
      {/* Header */}
      <div style={{
        background: "#1a1a2e", padding: "8px 12px", flexShrink: 0,
        borderBottom: "1px solid #1e293b",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span style={{ color: "#a9b1d6", fontWeight: 700, fontSize: 12, fontFamily: "monospace" }}>
          🐛 Debug — {mode === "motor" ? "MOTOR" : "LOCAL"}
        </span>
        <button onClick={onClear} style={{
          background: "transparent", border: "1px solid #2d2d3d",
          color: "#6b7280", padding: "2px 8px", borderRadius: 4,
          cursor: "pointer", fontSize: 10, fontFamily: "monospace",
        }}>Limpiar</button>
      </div>

      {/* Leyenda */}
      <div style={{
        padding: "5px 10px", borderBottom: "1px solid #12121f",
        display: "flex", flexWrap: "wrap", gap: 4, flexShrink: 0,
      }}>
        {Object.entries(DBG).map(([k, v]) => (
          <span key={k} style={{
            fontSize: 8, color: v.color, background: v.bg,
            padding: "1px 4px", borderRadius: 3, fontFamily: "monospace",
          }}>{v.label}</span>
        ))}
      </div>

      {/* Entradas */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {log.length === 0 ? (
          <div style={{
            color: "#4b5563", padding: "24px 12px",
            textAlign: "center", fontSize: 11, fontFamily: "monospace",
          }}>Sin eventos aún.<br />Iniciá o reiniciá la conversación.</div>
        ) : (
          log.map(e => <DebugEntry key={e.id} entry={e} />)
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
export default function WhatsAppEmulator({ sharedFlow, botId, botName: botNameProp, initialData }) {
  const [hasFlow, setHasFlow]       = useState(false);
  const [messages, setMessages]     = useState([]);
  const [variables, setVariables]   = useState({});
  const [isTyping, setIsTyping]     = useState(false);
  const [waitingFor, setWaitingFor] = useState(null);
  const [pendingUI, setPendingUI]   = useState(null);
  const [inputText, setInputText]   = useState("");
  const [ended, setEnded]           = useState(false);
  const [showVars, setShowVars]     = useState(false);
  const [showDebug, setShowDebug]   = useState(false);
  const [localLog, setLocalLog]     = useState([]);
  const [motorLog, setMotorLog]     = useState([]);
  const [botName, setBotName]       = useState("Bot");
  const [useMotor, setUseMotor]     = useState(false);
  const [motorPhone]                = useState(() => `emu_${Date.now()}`);
  const sendMotorRef                = useRef(null); // evita dependencia de orden
  const useMotorRef                 = useRef(false); // ref para handleSubmit (evita closure stale)
  const localDebugRef               = useRef(null); // logging LOCAL, null cuando debug está off
  const motorDebugRef               = useRef(null); // logging MOTOR, null cuando debug está off

  // Refs for async engine (avoid stale closures)
  const flowRef    = useRef(null);
  const varsRef    = useRef({});
  const waitRef    = useRef(null);
  const endedRef   = useRef(false);
  const jsonFile   = useRef(null);
  const attachFile = useRef(null);
  const bottomRef  = useRef(null);
  const processRef = useRef(null); // holds processNode, updated each render

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping, pendingUI]);

  // Conectar/desconectar las funciones de debug según el toggle
  useEffect(() => {
    if (showDebug) {
      localDebugRef.current = (type, summary, details = null) => {
        setLocalLog(p => [...p, { id: uid(), time: timeStr(), type, summary, details }]);
      };
      motorDebugRef.current = (type, summary, details = null) => {
        setMotorLog(p => [...p, { id: uid(), time: timeStr(), type, summary, details }]);
      };
    } else {
      localDebugRef.current = null;
      motorDebugRef.current = null;
    }
  }, [showDebug]);

  // Cargar flujo cuando llega desde el diseñador
  useEffect(() => {
    if (sharedFlow) loadFlow(sharedFlow);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedFlow]);

  // Auto-cargar en modo LOCAL cuando hay initialData (sin necesidad de "Enviar al emulador")
  useEffect(() => {
    if (!useMotor && initialData && !sharedFlow) {
      loadFlow({ ...initialData, name: botNameProp || initialData.name });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  // ── Helpers ──────────────────────────────────────────────
  const addMsg = (from, payload) =>
    setMessages(p => [...p, { id: uid(), from, ...payload, time: timeStr() }]);

  const setVar = (name, value) => {
    if (!name) return;
    varsRef.current = { ...varsRef.current, [name]: String(value) };
    setVariables({ ...varsRef.current });
  };

  // Trace: registra { node, value } en __FLOW_TRACE__ (JSON array en vars)
  const pushTrace = (nodeName, value) => {
    if (!nodeName || value === null || value === undefined) return;
    if (String(value) === "#isimage") return;
    const arr = JSON.parse(varsRef.current["__FLOW_TRACE__"] || "[]");
    arr.push({ node: nodeName, value: String(value) });
    setVar("__FLOW_TRACE__", JSON.stringify(arr));
  };
  const traceLabel = (nodeId) => flowRef.current?.nodes?.find(n => n.id === nodeId)?.data?.label || null;

  const interp = text => interpolate(text, varsRef.current);

  const getNode    = id   => flowRef.current?.nodes.find(n => n.id === id);
  const followEdge = (nid, port = "default") =>
    flowRef.current?.edges.find(e => e.src === nid && e.sp === port)?.tgt ?? null;
  // Igual que el motor: prueba "default", luego "ok", luego cualquier edge
  const followDefault = (nid) =>
    followEdge(nid, "default") ??
    followEdge(nid, "ok") ??
    flowRef.current?.edges.find(e => e.src === nid)?.tgt ?? null;

  // ── Flow Engine ───────────────────────────────────────────
  // Using a ref so recursive async calls always use latest closure
  processRef.current = async function processNode(nodeId) {
    if (!nodeId || endedRef.current) return;
    const node = getNode(nodeId);
    if (!node) {
      localDebugRef.current?.("error", `Nodo no encontrado: ${nodeId}`);
      return;
    }
    const d = node.data;
    const dbg = localDebugRef.current; // alias corto

    switch (node.type) {

      case "startingPoint": {
        const next = followDefault(nodeId);
        dbg?.("node", `[startingPoint] → ${next ?? "null (sin edge)"}`);
        await processRef.current(next);
        break;
      }

      case "sendMessage": {
        setIsTyping(true); await sleep(750); setIsTyping(false);
        const smText = interp(d.message);
        addMsg("bot", { text: smText, image: d.imageUrl || null });
        dbg?.("msg", `"${smText?.slice(0, 60)}${smText?.length > 60 ? "…" : ""}"`);
        await sleep(200);
        await processRef.current(followDefault(nodeId));
        break;
      }

      case "media": {
        setIsTyping(true); await sleep(900); setIsTyping(false);
        addMsg("bot", {
          caption: interp(d.caption) || null,
          image:    d.mediaType === "image" ? d.mediaUrl || null : null,
          mediaUrl: d.mediaType !== "image" ? d.mediaUrl || null : null,
          mediaType: d.mediaType,
        });
        dbg?.("msg", `[media:${d.mediaType}] ${d.mediaUrl?.slice(0, 50)}`);
        await sleep(200);
        await processRef.current(followDefault(nodeId));
        break;
      }

      case "goodbye": {
        setIsTyping(true); await sleep(750); setIsTyping(false);
        const gbText = interp(d.message);
        addMsg("bot", { text: gbText });
        dbg?.("end", `goodbye — "${gbText?.slice(0, 60)}"`);
        endedRef.current = true;
        setEnded(true);
        setWaitingFor(null); waitRef.current = null;
        break;
      }

      case "replyButtons": {
        setIsTyping(true); await sleep(750); setIsTyping(false);
        const parts = [
          d.header && `*${interp(d.header)}*`,
          interp(d.body),
        ].filter(Boolean);
        if (parts.length) addMsg("bot", { text: parts.join("\n") });
        if (d.footer)     addMsg("bot", { text: `_${interp(d.footer)}_` });
        const mappedBtns = (d.buttons || []).map((b, i) => ({ id: `btn_${i}`, label: interp(b.label || b.title || `Opción ${i + 1}`) }));
        const wf = { type: "button", nodeId, saveResponseTo: d.saveResponseTo, buttons: mappedBtns };
        waitRef.current = wf; setWaitingFor(wf);
        setPendingUI({ type: "buttons", buttons: d.buttons || [] });
        dbg?.("wait", `replyButtons — ${(d.buttons||[]).length} botones`, (d.buttons||[]).map(b=>b.label));
        break;
      }

      case "listButtons": {
        setIsTyping(true); await sleep(750); setIsTyping(false);
        const lbBody = interp(d.body || d.message);
        const lbOpts = (d.sections || []).flatMap(s => s.options || []).map((o, i) => ({
          id: o.id || `opt_${i}`,
          title: interp(o.title || o.label || o.keyword || `Opción ${i + 1}`),
          desc: interp(o.desc || o.description || ""),
        }));
        if (lbOpts.length && lbBody) {
          setPendingUI({
            type: "list",
            body: lbBody,
            header: interp(d.header || ""),
            buttonText: d.buttonLabel || d.buttonText || "Ver opciones",
            sections: [{ title: "", options: lbOpts }],
          });
          const wf = { type: "list", nodeId, saveResponseTo: d.saveResponseTo, options: lbOpts };
          waitRef.current = wf; setWaitingFor(wf);
          dbg?.("wait", `listButtons — ${lbOpts.length} opciones`, lbOpts.map(o=>`${o.id}: ${o.title}`));
        } else {
          dbg?.("node", `listButtons sin opciones/body — skip`);
          await processRef.current(followDefault(nodeId));
        }
        break;
      }

      case "askQuestion":
      case "askEmail":
      case "askNumber":
      case "askPhone":
      case "askDate":
      case "askUrl":
      case "askAddress": {
        setIsTyping(true); await sleep(750); setIsTyping(false);
        const askQ = interp(d.question);
        addMsg("bot", { text: askQ });
        const wf = {
          type: "text", nodeId, saveResponseTo: d.saveResponseTo,
          nodeType: node.type,
          min: d.min ?? null, max: d.max ?? null,
          phoneFormat: d.phoneFormat || "any",
          errorMessage: d.errorMessage || null,
        };
        waitRef.current = wf; setWaitingFor(wf);
        dbg?.("wait", `${node.type} — guardar en {{${d.saveResponseTo||"—"}}}`, { question: askQ });
        break;
      }

      case "askFile": {
        setIsTyping(true); await sleep(750); setIsTyping(false);
        addMsg("bot", { text: interp(d.question) });
        const wf = { type: "file", nodeId, saveResponseTo: d.saveResponseTo };
        waitRef.current = wf; setWaitingFor(wf);
        dbg?.("wait", `askFile — guardar en {{${d.saveResponseTo||"—"}}}`);
        break;
      }

      case "conditions": {
        const ok = evalConditions(node, varsRef.current);
        const branch = ok ? "true" : "false";
        const nextCond = followEdge(nodeId, branch);
        dbg?.("branch", `conditions → ${branch} → ${nextCond ?? "null"}`,
          { conditions: d.conditions, logicOp: d.logicOp, vars: { ...varsRef.current }, result: ok });
        await processRef.current(nextCond);
        break;
      }

      case "setField": {
        const sfVal = interp(d.value);
        setVar(d.variable, sfVal);
        dbg?.("vars", `setField {{${d.variable}}} = "${sfVal}"`);
        await processRef.current(followDefault(nodeId));
        break;
      }

      case "formulas": {
        const fResult = evalFormula(d.formula, varsRef.current);
        setVar(d.resultVariable, fResult);
        dbg?.("vars", `formula {{${d.resultVariable}}} = ${fResult}`, { formula: d.formula });
        await processRef.current(followDefault(nodeId));
        break;
      }

      case "jumpTo":
        dbg?.("node", `jumpTo → ${d.targetNodeId ?? "null"}`);
        await processRef.current(d.targetNodeId);
        break;

      case "acdSkill":
        // Setea la habilidad ACD como variable interna y continúa
        if (d.skill) setVar("__ACD_SKILL__", d.skill);
        dbg?.("vars", `acdSkill → {{__ACD_SKILL__}} = "${d.skill}"`);
        await processRef.current(followEdge(nodeId, "default") ?? followDefault(nodeId));
        break;

      case "calendar": {
        // Pausar rama → siempre false
        if (d.paused) {
          dbg?.("branch", `calendar PAUSADO → false`);
          await processRef.current(followEdge(nodeId, "false") ?? followDefault(nodeId));
          break;
        }
        const now = new Date();
        const day  = now.getDay();
        const hhmm = now.toTimeString().slice(0, 5);
        const dateStr = now.toISOString().slice(0, 10);
        let calBranch = "true";
        let calReason = "en horario";
        if ((d.holidays || []).includes(dateStr)) { calBranch = "false"; calReason = `feriado ${dateStr}`; }
        else if (!(d.days || []).includes(day))  { calBranch = "false"; calReason = `día ${day} no habilitado`; }
        else {
          const calStart = d.timeStart || "00:00", calEnd = d.timeEnd || "23:59";
          if (hhmm < calStart || hhmm >= calEnd) { calBranch = "false"; calReason = `${hhmm} fuera de ${calStart}-${calEnd}`; }
        }
        dbg?.("branch", `calendar → ${calBranch} (${calReason})`,
          { day, hhmm, dateStr, days: d.days, timeStart: d.timeStart, timeEnd: d.timeEnd });
        await processRef.current(followEdge(nodeId, calBranch) ?? followDefault(nodeId));
        break;
      }

      case "keywordOptions": {
        const wf = { type: "keyword", nodeId, keywords: d.keywords || [], saveResponseTo: d.saveResponseTo };
        waitRef.current = wf; setWaitingFor(wf);
        dbg?.("wait", `keywordOptions — ${(d.keywords||[]).length} keywords`);
        break;
      }

      case "keywordJump": {
        const wf = { type: "keywordJump", nodeId, keywords: d.keywords || [] };
        waitRef.current = wf; setWaitingFor(wf);
        dbg?.("wait", `keywordJump — ${(d.keywords||[]).length} keywords`);
        break;
      }

      case "leadScoring": {
        const cur = parseFloat(varsRef.current[d.scoreVariable] || 0);
        const newScore = cur + (Number(d.scoreChange) || 0);
        setVar(d.scoreVariable, newScore);
        dbg?.("vars", `leadScoring {{${d.scoreVariable}}} ${cur} → ${newScore}`);
        await processRef.current(followDefault(nodeId));
        break;
      }

      case "goal": {
        setIsTyping(true); await sleep(400); setIsTyping(false);
        addMsg("bot", { text: `🚩 Objetivo alcanzado: ${d.goalName}` });
        dbg?.("node", `goal — "${d.goalName}"`);
        await processRef.current(followDefault(nodeId));
        break;
      }

      case "abTest": {
        const pct = Number(d.percentageA) || 50;
        const variant = Math.random() * 100 < pct ? "variantA" : "variantB";
        if (d.saveVariantTo) setVar(d.saveVariantTo, variant);
        dbg?.("branch", `abTest → ${variant} (${pct}% A)`);
        await processRef.current(followEdge(nodeId, variant));
        break;
      }

      case "sendTemplate": {
        setIsTyping(true); await sleep(750); setIsTyping(false);
        addMsg("bot", { text: `📄 Plantilla: ${d.templateName || "sin nombre"}` });
        dbg?.("msg", `sendTemplate: "${d.templateName}"`);
        await processRef.current(followDefault(nodeId));
        break;
      }

      case "contactSubscribe": {
        const tags = (d.tags || []).join(", ");
        if (tags) addMsg("bot", { text: `✅ Contacto suscrito: ${tags}` });
        dbg?.("vars", `contactSubscribe — tags: ${tags || "(ninguno)"}`);
        await processRef.current(followDefault(nodeId));
        break;
      }

      case "textMenu": {
        if (!d.message) {
          const next = followEdge(nodeId, "timeout") ?? followEdge(nodeId, "default") ?? followDefault(nodeId);
          dbg?.("node", `textMenu silencioso → ${next ?? "null"}`);
          await processRef.current(next);
          break;
        }
        setIsTyping(true); await sleep(600); setIsTyping(false);
        const tmOpts = (d.options || []).map(o => ({
          id: o.id,
          title: o.label || o.keyword || o.id,
          keyword: o.keyword || o.id,
          desc: "",
        }));
        // Opciones visibles en la lista: excluir keywords especiales (#isimage, etc.)
        const tmVisible = tmOpts.filter(o => !o.keyword.startsWith("#"));
        addMsg("bot", { text: interp(d.message) });
        if (tmVisible.length) {
          setPendingUI({
            type: "list",
            body: "",
            buttonText: "Ver opciones",
            sections: [{ title: "", options: tmVisible }],
          });
        }
        const wfTm = { type: "textMenu", nodeId, options: tmOpts, saveResponseTo: d.saveResponseTo };
        waitRef.current = wfTm; setWaitingFor(wfTm);
        dbg?.("wait", `textMenu — ${tmOpts.length} opciones`, tmOpts.map(o => `${o.id}: ${o.keyword}`));
        break;
      }

      case "transferAgent": {
        setIsTyping(true); await sleep(800); setIsTyping(false);
        addMsg("bot", { text: `🧑‍💼 Transfiriendo a agente${d.queue ? ` (${d.queue})` : ""}...` });
        await sleep(500);
        // Seguir "transferred" si tiene conexión, sino "noOperators"
        const transferredEdge = followEdge(nodeId, "transferred");
        if (transferredEdge) {
          await processRef.current(transferredEdge);
        } else {
          addMsg("bot", { text: "⚠️ [Emulador] No hay operadores disponibles en este momento." });
          await processRef.current(followEdge(nodeId, "noOperators") ?? followDefault(nodeId));
        }
        break;
      }

      case "loopControl": {
        const cur = parseInt(varsRef.current[d.variable] || "0", 10);
        const next = cur + 1;
        setVar(d.variable, String(next));
        const maxC = Number(d.maxCycles) || 3;
        await processRef.current(followEdge(nodeId, next >= maxC ? "complete" : "incomplete"));
        break;
      }

      case "aiBot": {
        setIsTyping(true); await sleep(1200); setIsTyping(false);
        addMsg("bot", { text: `🤖 [Emulador] Bot IA "${d.assistantName || "sin nombre"}" — respuesta simulada.` });
        if (d.outputVariable) setVar(d.outputVariable, "[respuesta IA simulada]");
        if (d.disconnectVar) setVar(d.disconnectVar, "false");
        if (d.assistanceVar) setVar(d.assistanceVar, "false");
        // Intentar ok → disconnect → default en ese orden
        const aiBotNext = followEdge(nodeId, "ok") ?? followEdge(nodeId, "disconnect") ?? followEdge(nodeId, "default") ?? followDefault(nodeId);
        await processRef.current(aiBotNext);
        break;
      }

      case "groqChat": {
        const aiCfg = flowRef.current?.aiConfig || {};
        const exitKw = aiCfg.exitKeyword || "salir";
        if (!aiCfg.apiKey) {
          addMsg("bot", { text: "⚠️ Agente IA sin configurar. Cargá la API Key en la configuración del bot." });
          dbg?.("error", `groqChat — sin apiKey`);
          await processRef.current(followEdge(nodeId, "error") ?? followDefault(nodeId));
          break;
        }
        setIsTyping(true); await sleep(800); setIsTyping(false);
        addMsg("bot", { text: `💬 Agente IA conectado. Escribí tu consulta o *"${exitKw}"* para terminar.` });
        const docs = aiCfg.knowledgeDocs || [];
        const docsText = docs.length
          ? "\n\n--- BASE DE CONOCIMIENTO ---\n" + docs.map(doc => `[${doc.name}]\n${doc.content}`).join("\n\n")
          : "";
        const wf = {
          type: "groqChat", nodeId,
          apiKey: aiCfg.apiKey,
          model: aiCfg.model || "llama-3.1-8b-instant",
          systemPrompt: (aiCfg.systemPrompt || "") + docsText,
          outputVariable: d.outputVariable || "",
          exitKeyword: exitKw,
          endPhrase: aiCfg.endPhrase || "",
          maxTokens: aiCfg.maxTokens || 500,
          history: [],
        };
        waitRef.current = wf; setWaitingFor(wf);
        dbg?.("wait", `groqChat — modelo: ${aiCfg.model || "llama-3.1-8b-instant"}, exit: "${exitKw}"`);
        break;
      }

      case "dbQuery": {
        setIsTyping(true); await sleep(600); setIsTyping(false);
        const procName = d.query || d.procedure || "";
        const bdData   = flowRef.current?.bd;
        const proc     = bdData?.procedures?.find(p => p.name === procName);
        if (proc) {
          // Si tiene filas las usa, si no devuelve { DIA: "1" } como mock por defecto
          const row = proc.rows?.length ? proc.rows[0] : { DIA: "1" };
          for (const field of (d.outputFields || [])) {
            setVar(field.name, row[field.name] !== undefined ? String(row[field.name]) : "1");
          }
          if (d.saveResponseTo) {
            setVar(d.saveResponseTo, row[d.saveResponseTo] !== undefined ? String(row[d.saveResponseTo]) : "1");
          }
          dbg?.("node", `dbQuery "${procName}" → success`, row);
          await processRef.current(followEdge(nodeId, "success") ?? followEdge(nodeId, "default") ?? followDefault(nodeId));
        } else {
          dbg?.("error", `dbQuery "${procName}" → no encontrado en BD Mock`);
          await processRef.current(followEdge(nodeId, "error") ?? followEdge(nodeId, "success") ?? followEdge(nodeId, "default") ?? followDefault(nodeId));
        }
        break;
      }

      case "flowControl": {
        const mappings = d.mappings || [];
        mappings.forEach(m => {
          if (!m.traceName || !m.targetVar) return;
          const traceArr = JSON.parse(varsRef.current["__FLOW_TRACE__"] || "[]");
          const entry = traceArr.find(e => e.node === m.traceName);
          if (entry !== undefined) setVar(m.targetVar, entry.value);
        });
        dbg?.("node", `flowControl — ${mappings.length} mapeo(s)`);
        await processRef.current(followDefault(nodeId));
        break;
      }

      case "integrationEntry":
      case "integrationExit":
        dbg?.("node", `${node.type} — pass-through`);
        await processRef.current(followDefault(nodeId));
        break;

      case "apiCall": {
        const url = interp(d.url || "");
        const method = d.method || "GET";
        if (!url) {
          dbg?.("error", "apiCall sin URL — error");
          await processRef.current(followEdge(nodeId, "error") ?? followDefault(nodeId));
          break;
        }
        try {
          const headers = {};
          try { Object.assign(headers, JSON.parse(interp(d.headers || "{}"))); } catch {}
          if (d.authType === "bearer" && d.authValue)
            headers["Authorization"] = `Bearer ${interp(d.authValue)}`;
          else if (d.authType === "basic" && d.authValue)
            headers["Authorization"] = `Basic ${btoa(interp(d.authValue))}`;
          else if (d.authType === "apikey" && d.authValue) {
            const [hName, ...hVal] = interp(d.authValue).split(":");
            if (hName) headers[hName.trim()] = hVal.join(":").trim();
          }
          const hasBody = ["POST", "PUT", "PATCH"].includes(method);
          const requestBody = hasBody && d.body ? interp(d.body) : undefined;

          // Rutar por el proxy server-side para evitar CORS
          const proxyResp = await fetch("/api/proxy", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ url, method, headers, requestBody, timeout: Number(d.timeout) || 10 }),
          });
          const rawText = await proxyResp.text();
          let proxyData;
          try { proxyData = JSON.parse(rawText); }
          catch { proxyData = { error: rawText.substring(0, 300), status: null, ok: false, body: null }; }

          if (proxyData.error && !proxyData.status) {
            dbg?.("error", `apiCall error: ${proxyData.error}`);
            await processRef.current(followEdge(nodeId, "error") ?? followDefault(nodeId));
            break;
          }

          const resultStr = proxyData.body ?? "";
          if (d.saveResponseTo) setVar(d.saveResponseTo, resultStr);
          dbg?.("node", `apiCall ${method} ${url.slice(0, 50)} → ${proxyData.status}`);
          await processRef.current(proxyData.ok
            ? (followEdge(nodeId, "success") ?? followDefault(nodeId))
            : (followEdge(nodeId, "error") ?? followDefault(nodeId))
          );
        } catch (err) {
          dbg?.("error", `apiCall error: ${err.message}`);
          await processRef.current(followEdge(nodeId, "error") ?? followDefault(nodeId));
        }
        break;
      }

      // globalKeywords is intercepted at input time, skip here
      case "globalKeywords":
        dbg?.("node", `globalKeywords — interceptado en input, skip`);
        await processRef.current(followDefault(nodeId));
        break;

      default:
        dbg?.("node", `[${node.type}] — tipo no manejado, skip`);
        await processRef.current(followDefault(nodeId));
        break;
    }
  };

  // ── User input handler ────────────────────────────────────
  const handleSubmit = useCallback(async (text, imgData) => {
    // Modo motor: delegar todo al backend (usar ref para evitar closure stale)
    if (useMotorRef.current) {
      const motorText = imgData ? "#isimage" : text?.trim();
      if (!motorText || endedRef.current) return;
      addMsg("user", { text: text || null, image: imgData || null });
      setInputText("");
      setWaitingFor(null);
      await sendMotorRef.current?.(motorText);
      return;
    }

    if (endedRef.current || !waitRef.current) return;
    const w = waitRef.current;

    // Global keywords intercept
    if (text && flowRef.current) {
      const gk = flowRef.current.nodes.find(n => n.type === "globalKeywords");
      if (gk) {
        const hit = (gk.data.keywords || []).find(k =>
          k.keyword && text.toLowerCase().includes(k.keyword.toLowerCase())
        );
        if (hit?.targetNodeId) {
          addMsg("user", { text });
          waitRef.current = null; setWaitingFor(null); setPendingUI(null); setInputText("");
          await processRef.current(hit.targetNodeId);
          return;
        }
      }
    }

    // #isimage es el keyword especial de CyT para detectar envío de imagen
    const effectiveText = imgData ? "#isimage" : (text || "");
    addMsg("user", { text: text || null, image: imgData || null });
    setInputText("");

    if (w.type === "text" || w.type === "file") {
      if (w.saveResponseTo) setVar(w.saveResponseTo, effectiveText);
      // Trace: registra la respuesta del usuario si el nodo tiene label
      pushTrace(traceLabel(w.nodeId), effectiveText);
      waitRef.current = null; setWaitingFor(null);
      // Si viene de textMenu con #isimage, enruta por el edge de esa opción
      const nextFile = w.imageOptId
        ? followEdge(w.nodeId, w.imageOptId) ?? followDefault(w.nodeId)
        : followDefault(w.nodeId);
      await processRef.current(nextFile);

    } else if (w.type === "keyword") {
      const hit = (w.keywords || []).find(k =>
        k.keyword && text.toLowerCase().includes(k.keyword.toLowerCase())
      );
      if (w.saveResponseTo) setVar(w.saveResponseTo, text);
      waitRef.current = null; setWaitingFor(null);
      await processRef.current(followEdge(w.nodeId, hit ? "match" : "noMatch"));

    } else if (w.type === "keywordJump") {
      const hit = (w.keywords || []).find(k =>
        k.keyword && text.toLowerCase().includes(k.keyword.toLowerCase())
      );
      waitRef.current = null; setWaitingFor(null);
      await processRef.current(followEdge(w.nodeId, hit ? "match" : "noMatch"));

    } else if (w.type === "textMenu") {
      const hit = (w.options || []).find(o =>
        o.keyword && effectiveText.toLowerCase().trim() === o.keyword.toLowerCase().trim()
      );
      if (w.saveResponseTo) setVar(w.saveResponseTo, effectiveText);
      // Trace: usa el label de la opción encontrada o el texto tal cual
      pushTrace(traceLabel(w.nodeId), hit ? (hit.title || hit.keyword || effectiveText) : effectiveText);
      waitRef.current = null; setWaitingFor(null);
      await processRef.current(followEdge(w.nodeId, hit ? hit.id : "invalid"));

    } else if (w.type === "listButtons") {
      // Usuario escribió la keyword de un botón de lista
      const hit = (w.options || []).find(o =>
        o.keyword && text.toLowerCase().trim() === o.keyword.toLowerCase().trim()
      );
      waitRef.current = null; setWaitingFor(null); setPendingUI(null);
      await processRef.current(followEdge(w.nodeId, hit ? hit.id : "error"));

    } else if (w.type === "groqChat") {
      // Palabra clave de salida
      if (text.toLowerCase().trim() === w.exitKeyword.toLowerCase().trim()) {
        waitRef.current = null; setWaitingFor(null);
        await processRef.current(followEdge(w.nodeId, "ok") ?? followEdge(w.nodeId));
        return;
      }
      // Llamada a Groq API
      setIsTyping(true);
      try {
        const messages = [
          ...(w.systemPrompt ? [{ role: "system", content: w.systemPrompt }] : []),
          ...w.history,
          { role: "user", content: text },
        ];
        const resp = await fetch("https://api.groq.com/openai/v1/chat/completions", {
          method: "POST",
          headers: { "Authorization": `Bearer ${w.apiKey}`, "Content-Type": "application/json" },
          body: JSON.stringify({ model: w.model, messages, max_tokens: w.maxTokens }),
        });
        const data = await resp.json();
        setIsTyping(false);
        if (!resp.ok) {
          addMsg("bot", { text: `⚠️ Error Groq (${resp.status}): ${data.error?.message || "sin detalles"}` });
          waitRef.current = null; setWaitingFor(null);
          await processRef.current(followEdge(w.nodeId, "error") ?? followEdge(w.nodeId));
          return;
        }
        const rawAiText = convertEmotions(data.choices?.[0]?.message?.content || "[sin respuesta]");

        // Detectar frase de corte del agente
        const endPhraseMatch = w.endPhrase &&
          rawAiText.toLowerCase().includes(w.endPhrase.toLowerCase());
        const aiText = endPhraseMatch
          ? rawAiText.replace(new RegExp(w.endPhrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), "").trim()
          : rawAiText;

        if (w.outputVariable) setVar(w.outputVariable, aiText);
        w.history.push({ role: "user", content: text });
        w.history.push({ role: "assistant", content: rawAiText });
        addMsg("bot", { text: aiText });

        if (endPhraseMatch) {
          waitRef.current = null; setWaitingFor(null);
          await processRef.current(followEdge(w.nodeId, "ok") ?? followEdge(w.nodeId));
          return;
        }
        // Sigue esperando (loop de conversación)
      } catch (err) {
        setIsTyping(false);
        addMsg("bot", { text: `⚠️ Error de conexión: ${err.message}` });
        waitRef.current = null; setWaitingFor(null);
        await processRef.current(followEdge(w.nodeId, "error") ?? followEdge(w.nodeId));
      }
    }
  }, []);

  const handleOptionSelect = useCallback(async (opt) => {
    // Modo motor: mandar el ID de la opción al backend
    if (useMotor) {
      addMsg("user", { text: opt.title || opt.label });
      setWaitingFor(null);
      setPendingUI(null);
      await sendMotorRef.current?.(opt.id);
      return;
    }
    // Modo local
    const w = waitRef.current;
    if (!w) return;
    addMsg("user", { text: opt.title || opt.label });
    if (w.saveResponseTo) setVar(w.saveResponseTo, opt.title || opt.label);
    // Trace: registra la opción seleccionada si el nodo tiene label
    pushTrace(traceLabel(w.nodeId), opt.title || opt.label);
    waitRef.current = null; setWaitingFor(null); setPendingUI(null);
    let next = followEdge(w.nodeId, opt.id);
    if (!next) next = followEdge(w.nodeId, "any");
    await processRef.current(next);
  }, [useMotor]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load flow ─────────────────────────────────────────────
  const loadFlow = useCallback((data) => {
    flowRef.current = data;
    const initVars = {};
    (data.vars || []).forEach(v => { if (v.name && v.defaultValue) initVars[v.name] = v.defaultValue; });
    varsRef.current  = initVars;
    endedRef.current = false;
    waitRef.current  = null;
    setHasFlow(true);
    setBotName(data.name || "Bot");
    setMessages([]);
    setVariables(initVars);
    setEnded(false);
    setWaitingFor(null);
    setPendingUI(null);
    setInputText("");
    const start = data.nodes?.find(n => n.type === "startingPoint");
    if (start) setTimeout(() => processRef.current(start.id), 600);
  }, []);

  const restart = useCallback(() => {
    if (useMotor) startMotor();
    else if (flowRef.current) loadFlow(flowRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFlow, useMotor]);

  // ── Motor (backend engine) ────────────────────────────────
  const motorReset = useCallback(async (bid) => {
    setMessages([]); setVariables({}); setEnded(false); endedRef.current = false;
    setWaitingFor(null); setPendingUI(null);
    setIsTyping(true);
    const t0 = Date.now();
    const reqBody = { phone: motorPhone, botId: bid, reset: true };
    motorDebugRef.current?.("req", `RESET botId=${bid}`, reqBody);
    try {
      const res = await fetch("/api/engine", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reqBody),
      });
      const data = await res.json();
      setIsTyping(false);
      motorDebugRef.current?.("res",
        `${res.status} ${res.ok ? "OK" : "ERR"} — ${data.messages?.length ?? 0} msgs, waitingFor=${data.waitingFor ?? "null"}, ended=${data.ended} (${Date.now()-t0}ms)`,
        data);
      renderMotorResponse(data);
    } catch (err) {
      setIsTyping(false);
      motorDebugRef.current?.("error", `RESET error: ${err.message}`);
      setMessages(p => [...p, { id: uid(), from: "bot", text: "⚠️ Error al conectar con el motor: " + err.message, time: timeStr() }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motorPhone]);

  const sendMotor = useCallback(async (message) => {
    setIsTyping(true);
    const t0 = Date.now();
    const reqBody = { phone: motorPhone, botId: botId, message };
    motorDebugRef.current?.("req", `MSG "${String(message).slice(0, 40)}"`, reqBody);
    try {
      const res = await fetch("/api/engine", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reqBody),
      });
      const data = await res.json();
      setIsTyping(false);
      motorDebugRef.current?.("res",
        `${res.status} ${res.ok ? "OK" : "ERR"} — ${data.messages?.length ?? 0} msgs, waitingFor=${data.waitingFor ?? "null"}, ended=${data.ended} (${Date.now()-t0}ms)`,
        data);
      renderMotorResponse(data);
    } catch (err) {
      setIsTyping(false);
      motorDebugRef.current?.("error", `sendMotor error: ${err.message}`);
      setMessages(p => [...p, { id: uid(), from: "bot", text: "⚠️ Error: " + err.message, time: timeStr() }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motorPhone, botId]);

  // Mantener refs actualizadas para evitar dependencias de orden
  useEffect(() => { sendMotorRef.current = sendMotor; }, [sendMotor]);
  useEffect(() => { useMotorRef.current = useMotor; }, [useMotor]);

  const renderMotorResponse = useCallback((data) => {
    if (!data?.messages) return;

    const textMsgs = [];
    let interactiveUI = null;

    for (const m of data.messages) {
      if (m.type === "text") {
        textMsgs.push({ id: uid(), from: "bot", text: m.text.body, time: timeStr() });
      } else if (m.type === "image" || m.type === "video" || m.type === "document") {
        const media = m[m.type] || {};
        textMsgs.push({
          id: uid(), from: "bot", mediaType: m.type, time: timeStr(),
          image:    m.type === "image" ? (media.link || null) : null,
          mediaUrl: m.type !== "image" ? (media.link || null) : null,
          caption:  media.caption || "",
        });
      } else if (m.type === "interactive") {
        const ia = m.interactive;
        if (ia.type === "button") {
          const buttons = ia.action.buttons.map(b => ({ id: b.reply.id, label: b.reply.title, title: b.reply.title }));
          textMsgs.push({ id: uid(), from: "bot", text: ia.body?.text || "", time: timeStr() });
          interactiveUI = { type: "buttons", buttons };
        } else if (ia.type === "list") {
          const rows = ia.action.sections?.flatMap(s => s.rows) || [];
          const opts = rows.map(r => ({ id: r.id, label: r.title, title: r.title, desc: r.description }));
          // El cuerpo del mensaje va dentro del ListSheet (body), no como burbuja separada
          interactiveUI = {
            type: "list",
            body: ia.body?.text || "",
            buttonText: ia.action.button || "Ver opciones",
            sections: [{ title: "", options: opts }],
          };
        }
      }
    }

    // Mostrar traza de ejecución del motor en el debug panel
    if (data.trace) {
      for (const t of data.trace) {
        motorDebugRef.current?.("node", `[${t.type}] ${t.id ?? ""}${t.msg ? " — " + t.msg : ""}`, Object.keys(t).length > 2 ? t : null);
      }
    }

    setMessages(p => [...p, ...textMsgs]);
    setVariables(data.vars || {});

    if (data.ended) {
      endedRef.current = true;
      setEnded(true);
      setWaitingFor(null);
      setPendingUI(null);
    } else if (interactiveUI) {
      setPendingUI(interactiveUI);
      setWaitingFor({ type: data.waitingFor || interactiveUI.type });
    } else if (data.waitingFor) {
      setWaitingFor({ type: data.waitingFor });
      setPendingUI(null);
    }
  }, []);

  const startMotor = useCallback(() => {
    if (botId) motorReset(botId);
  }, [botId, motorReset]);

  // Arrancar motor automáticamente si useMotor está activo y hay botId
  useEffect(() => {
    if (useMotor && botId) startMotor();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMotor]);

  const onJsonFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = ev => {
      try { loadFlow(JSON.parse(ev.target.result)); }
      catch (err) { alert("Error al leer el JSON: " + err.message); }
    };
    r.readAsText(f); e.target.value = "";
  };

  // ── Derived UI state ──────────────────────────────────────
  const canType = (waitingFor?.type === "text" || waitingFor?.type === "groqChat" || waitingFor?.type === "groq") && !ended;
  const canFile = (waitingFor?.type === "file" || waitingFor?.type === "textMenu") && !ended;
  const waiting = (waitingFor?.type === "keyword" || waitingFor?.type === "keywordJump" || waitingFor?.type === "textMenu" || waitingFor?.type === "listButtons") && !ended;

  // ── Render ────────────────────────────────────────────────
  return (
    <div style={{
      width: "100%", height: "100vh",
      background: "#111b21",
      display: "flex", alignItems: "center",
      justifyContent: "center",
      gap: 0,
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      overflowX: "auto", overflowY: "hidden",
    }}>
      <style>{`
        @keyframes wabounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }
        @keyframes msgIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .wa-msg { animation: msgIn 0.18s ease; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 4px; }
      `}</style>

      {/* ── Debug Panel LOCAL (izquierda) ── */}
      {showDebug && (
        <DebugPanel
          log={localLog}
          onClear={() => setLocalLog([])}
          mode="local"
          side="left"
        />
      )}

      {/* Phone frame */}
      <div style={{
        width: 390, height: "88vh", maxHeight: 760,
        background: "#fff", borderRadius: 32,
        boxShadow: "0 30px 70px rgba(0,0,0,0.55), 0 0 0 9px #1c1c1e",
        overflow: "hidden", display: "flex", flexDirection: "column",
      }}>

        {/* ── WhatsApp Header ── */}
        <div style={{
          background: "#075E54", padding: "10px 14px",
          display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%", background: "#25D366",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
          }}>🤖</div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: "#fff", fontWeight: 700, fontSize: 15,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{botName}</div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 12 }}>
              {isTyping ? "escribiendo..." : ended ? "conversación finalizada" : "en línea"}
            </div>
          </div>

          <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
            {botId && (
              <button
                onClick={() => setUseMotor(m => !m)}
                title={useMotor ? "Modo Motor activo — click para volver a local" : "Activar Modo Motor (backend)"}
                style={{
                  ...hdrBtn,
                  background: useMotor ? "#25D366" : "rgba(255,255,255,0.15)",
                  color: "#fff", fontWeight: 700, fontSize: 10,
                  padding: "3px 8px", borderRadius: 10,
                }}
              >
                {useMotor ? "⚡ MOTOR" : "⚡ LOCAL"}
              </button>
            )}
            <button onClick={() => setShowVars(v => !v)} title="Variables activas"
              style={hdrBtn}>{`{ }`}</button>
            {botId && (
              <button
                onClick={() => { setShowDebug(d => !d); setLocalLog([]); setMotorLog([]); }}
                title={showDebug ? "Cerrar debug" : "Abrir panel de debug"}
                style={{
                  ...hdrBtn,
                  background: showDebug ? "rgba(251,191,36,0.25)" : "rgba(255,255,255,0.08)",
                  color: showDebug ? "#fbbf24" : "rgba(255,255,255,0.6)",
                  fontWeight: 700, fontSize: 13,
                }}
              >🐛</button>
            )}
            {(hasFlow || useMotor) && (
              <button onClick={restart} title="Reiniciar" style={hdrBtn}>↺</button>
            )}
            {!useMotor && (
              <button onClick={() => jsonFile.current?.click()} title="Cargar flujo JSON" style={hdrBtn}>📂</button>
            )}
          </div>

          <input ref={jsonFile} type="file" accept=".json" onChange={onJsonFile} style={{ display: "none" }} />
        </div>

        {/* ── Variables panel ── */}
        {showVars && (
          <div style={{
            background: "#f0f7f4", borderBottom: "1px solid #c8e6c9",
            padding: "8px 14px", maxHeight: 130, overflowY: "auto", flexShrink: 0,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#2e7d32", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Variables activas
            </div>
            {Object.keys(variables).length === 0
              ? <div style={{ fontSize: 11, color: "#999" }}>Ninguna asignada aún</div>
              : Object.entries(variables).map(([k, v]) => (
                <div key={k} style={{ fontSize: 11, display: "flex", gap: 8, marginBottom: 3, alignItems: "baseline" }}>
                  <code style={{ color: "#1b5e20", fontWeight: 700 }}>{`{{${k}}}`}</code>
                  <span style={{ color: "#555" }}>= "{v}"</span>
                </div>
              ))
            }
          </div>
        )}

        {/* ── Empty state ── */}
        {!hasFlow ? (
          <div style={{
            flex: 1, background: "#e5ddd5",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 18, padding: 32, textAlign: "center",
          }}>
            <div style={{ fontSize: 54 }}>📱</div>
            <div style={{ color: "#666", fontSize: 14, lineHeight: 1.65 }}>
              Carga el JSON exportado<br />
              desde el <strong>Diseñador de Bot</strong><br />
              para simular la conversación
            </div>
            <button onClick={() => jsonFile.current?.click()} style={loadBtn}>
              📂 Cargar flujo JSON
            </button>
          </div>

        ) : (
          <>
            {/* ── Chat area ── */}
            <div style={{
              flex: 1, overflowY: "auto", padding: "12px 10px",
              background: "#e5ddd5",
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 16H9v-2h5V9.87a4 4 0 1 1 2 0V14h5v2h-5v15.95A10 10 0 0 0 23.66 27l-3.46-2 8.2-4.74-1.61 9.38A10 10 0 0 0 22 27.3V24h-8v-8zm40 40h-5v-2h5v-4.13a4 4 0 1 1 2 0V54h5v2h-5v15.95A10 10 0 0 0 63.66 67l-3.46-2 8.2-4.74-1.61 9.38A10 10 0 0 0 62 67.3V64h-8v-8z' fill='%23bdb5a6' fill-opacity='0.08' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            }}>
              {messages.map(msg => (
                <div key={msg.id} className="wa-msg">
                  {msg.from === "bot" ? <BotBubble msg={msg} /> : <UserBubble msg={msg} />}
                </div>
              ))}

              {isTyping && <TypingDots />}

              {pendingUI?.type === "buttons" && (
                <div className="wa-msg">
                  <ReplyBtns buttons={pendingUI.buttons} onSelect={handleOptionSelect} />
                </div>
              )}
              {pendingUI?.type === "list" && (
                <div className="wa-msg">
                  <ListSheet data={pendingUI} onSelect={handleOptionSelect} />
                </div>
              )}

              {ended && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  margin: "16px 0 6px", color: "#999", fontSize: 12,
                }}>
                  <div style={{ flex: 1, height: 1, background: "#c5b9ae" }} />
                  Fin de la conversación
                  <div style={{ flex: 1, height: 1, background: "#c5b9ae" }} />
                </div>
              )}

              {ended && (
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                  <button onClick={restart} style={loadBtn}>↺ Reiniciar</button>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* ── Input area — igual que WhatsApp real ── */}
            <div style={{
              background: "#f0f0f0", borderTop: "1px solid #e0e0e0",
              padding: "8px 10px", display: "flex", alignItems: "center",
              gap: 8, flexShrink: 0,
            }}>
              {/* Botón adjuntar — siempre visible, igual que en WhatsApp */}
              <button
                onClick={() => attachFile.current?.click()}
                title="Adjuntar archivo o imagen"
                disabled={ended}
                style={{
                  width: 38, height: 38, borderRadius: "50%", border: "none",
                  background: "transparent", color: ended ? "#bbb" : "#8696a0",
                  fontSize: 20, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: ended ? "default" : "pointer",
                }}
                onMouseOver={e => { if (!ended) e.currentTarget.style.color = "#555"; }}
                onMouseOut={e => { e.currentTarget.style.color = ended ? "#bbb" : "#8696a0"; }}
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                  <path d="M1.816 15.556v.002c0 1.502.584 2.912 1.646 3.972s2.472 1.647 3.974 1.647a5.58 5.58 0 0 0 3.972-1.645l9.547-9.548c.769-.768 1.147-1.767 1.058-2.817-.079-.968-.548-1.927-1.319-2.698-1.594-1.592-4.068-1.711-5.517-.262l-7.916 7.915c-.881.881-.792 2.25.214 3.261.959.958 2.423 1.053 3.263.215l5.511-5.512c.28-.28.267-.722.053-.936l-.244-.244c-.191-.191-.567-.349-.957.04l-5.506 5.506c-.18.18-.635.127-.976-.214-.098-.097-.576-.613-.213-.973l7.915-7.917c.818-.817 2.267-.699 3.23.262.5.501.802 1.1.849 1.685.051.573-.156 1.111-.589 1.543l-9.547 9.549a3.97 3.97 0 0 1-2.829 1.171 3.975 3.975 0 0 1-2.83-1.173 3.973 3.973 0 0 1-1.172-2.828c0-1.071.415-2.076 1.172-2.83l7.209-7.211c.157-.157.264-.579.028-.814L11.5 4.36a.572.572 0 0 0-.834.018L3.458 11.79a5.573 5.573 0 0 0-1.642 3.766z"/>
                </svg>
              </button>
              <input ref={attachFile} type="file" accept="image/*,application/pdf,video/*" onChange={e => {
                const f = e.target.files[0]; if (!f) return;
                const r = new FileReader();
                r.onload = ev => handleSubmit(f.name, f.type.startsWith("image/") ? ev.target.result : null);
                r.readAsDataURL(f); e.target.value = "";
              }} style={{ display: "none" }} />

              <input
                type="text"
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && (canType || waiting || canFile) && inputText.trim()) {
                    handleSubmit(inputText.trim());
                  }
                }}
                placeholder={
                  ended                      ? "Conversación finalizada" :
                  canType || waiting || canFile ? "Escribe un mensaje..."   :
                                               "Esperando al bot..."
                }
                disabled={!(canType || waiting || canFile)}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 24,
                  border: "none",
                  background: (canType || waiting || canFile) ? "#fff" : "#f5f5f5",
                  fontSize: 14, outline: "none", color: "#111",
                }}
              />
              <button
                onClick={() => {
                  if ((canType || waiting || canFile) && inputText.trim()) handleSubmit(inputText.trim());
                }}
                disabled={!(canType || waiting || canFile) || !inputText.trim()}
                style={{
                  width: 42, height: 42, borderRadius: "50%", border: "none",
                  background: (canType || waiting || canFile) && inputText.trim() ? "#25D366" : "#ccc",
                  color: "#fff", fontSize: 18, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: (canType || waiting || canFile) && inputText.trim() ? "pointer" : "default",
                  transition: "background 0.15s",
                }}
              >➤</button>
            </div>
          </>
        )}
      </div>

      {/* ── Debug Panel MOTOR (derecha) ── */}
      {showDebug && (
        <DebugPanel
          log={motorLog}
          onClear={() => setMotorLog([])}
          mode="motor"
          side="right"
        />
      )}
    </div>
  );
}

// ── Shared button styles ──────────────────────────────────────
const hdrBtn = {
  background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 6,
  color: "#fff", padding: "4px 8px", cursor: "pointer", fontSize: 12,
  fontFamily: "monospace",
};

const loadBtn = {
  background: "#25D366", color: "#fff", border: "none",
  borderRadius: 24, padding: "11px 26px", fontSize: 14,
  fontWeight: 700, cursor: "pointer",
  boxShadow: "0 4px 14px rgba(37,211,102,0.35)",
};
